import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useProjectCosts } from "../../api/costs.js";
import { UseBar } from "./budget.js";
import { useMoney } from "./shared.js";

/**
 * The project's month in two numbers — month to date and forecast — with a
 * link to its costs page; on the overview and in the dashboard's side panel.
 */
export function MonthSummary({
  projectId,
  compact = false,
}: {
  projectId: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const costs = useProjectCosts(projectId);
  const fmt = useMoney(costs.data?.currency);
  const value = (amount: string | null | undefined) =>
    amount === null || amount === undefined ? "—" : fmt(amount);
  const budget = costs.data?.budget ?? null;
  const percent = budget ? (budget.forecastPercent ?? budget.usedPercent) : 0;
  return (
    <div className={compact ? "flex flex-col gap-2" : "flex flex-col gap-3"}>
      <dl className="grid grid-cols-[1fr_auto] gap-y-2 text-sm">
        <dt className="text-muted">{t("costs.monthToDate")}</dt>
        <dd className="text-right font-medium tabular-nums">{value(costs.data?.monthToDate)}</dd>
        <dt className="text-muted">{t("costs.forecast")}</dt>
        <dd className="text-right tabular-nums">{value(costs.data?.forecast)}</dd>
      </dl>
      {budget && (
        <div className="flex flex-col gap-1 text-sm">
          <span className="flex justify-between gap-3">
            <span className="text-muted">{t("budgets.title")}</span>
            <span className="tabular-nums">
              {t("budgets.summary", { amount: fmt(budget.amount), percent: Math.round(percent) })}
            </span>
          </span>
          <UseBar percent={percent} label={t("budgets.forecastUse")} />
        </div>
      )}
      <Link
        to="/projects/$projectId/costs"
        params={{ projectId }}
        className="inline-flex min-h-6 items-center gap-1 self-start text-sm text-accent hover:underline"
      >
        {t("costs.open")}
        <ArrowUpRight className="size-4" aria-hidden />
      </Link>
    </div>
  );
}
